import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  api,
  ApiError,
  type FoodQueueRow,
  type FoodRow,
  type FoodParseResponse,
  type OffItem,
  type OffNutrients,
  type QuickFoodResponse,
} from "../lib/api";
import { fmtKcal, fmtTimeShort } from "../lib/format";
import { PageHeader, EmptyState } from "../components/EmptyState";
import { ConfirmFoodModal } from "../components/ConfirmFoodModal";
import { BarcodeScanner } from "../components/BarcodeScanner";

type Meal = "breakfast" | "lunch" | "dinner" | "snack";
const MEALS: Meal[] = ["breakfast", "lunch", "dinner", "snack"];

/** Downsize to ≤1280px / JPEG q0.85 before upload (SPEC §11) — keeps the
 *  vision call cheap and fast. Falls back to the original file on any failure. */
async function downsize(file: File): Promise<Blob> {
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, 1280 / Math.max(bitmap.width, bitmap.height));
    const w = Math.round(bitmap.width * scale);
    const h = Math.round(bitmap.height * scale);
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0, w, h);
    return await new Promise<Blob>((resolve) =>
      canvas.toBlob((b) => resolve(b ?? file), "image/jpeg", 0.85),
    );
  } catch {
    return file;
  }
}

export default function Food() {
  const nav = useNavigate();
  const fileRef = useRef<HTMLInputElement>(null);
  const queuePhotoRef = useRef<HTMLInputElement>(null);

  const [meal, setMeal] = useState<Meal>("snack");
  const [pending, setPending] = useState<FoodParseResponse | null>(null);
  const [aiBusy, setAiBusy] = useState<null | "photo" | "voice">(null);
  const [aiErr, setAiErr] = useState<string | null>(null);
  const [voiceText, setVoiceText] = useState("");

  // manual form
  const [desc, setDesc] = useState("");
  const [cal, setCal] = useState("");
  const [protein, setProtein] = useState("");
  const [carbs, setCarbs] = useState("");
  const [fat, setFat] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [recent, setRecent] = useState<FoodRow[] | null>(null);
  const [idemKey] = useState(() => crypto.randomUUID());

  // Quick add, database search and the parse queue — all keyless.
  const [quick, setQuick] = useState<QuickFoodResponse | null>(null);
  const [queue, setQueue] = useState<FoodQueueRow[]>([]);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState<OffItem[] | null | "searching">(null);
  const [scanning, setScanning] = useState(false);
  const [text, setText] = useState("");
  const [note, setNote] = useState<string | null>(null);

  const loadQuick = useCallback(() => {
    api.quickList().then(setQuick).catch(() => setQuick({ favourites: [], recents: [] }));
    api.foodQueue().then((r) => setQueue(r.queue)).catch(() => setQueue([]));
  }, []);

  function loadRecent() {
    api
      .listFood(20)
      .then((r) => setRecent(r.food))
      .catch(() => setRecent([]));
  }
  useEffect(loadRecent, []);
  useEffect(loadQuick, [loadQuick]);

  // Anything still waiting on the workstation parser is worth re-checking.
  useEffect(() => {
    if (!queue.some((q) => q.status === "pending")) return;
    const t = setInterval(loadQuick, 20_000);
    return () => clearInterval(t);
  }, [queue, loadQuick]);

  async function logAgain(id: number, from: "favourite" | "log", multiplier: number, label: string) {
    setNote(null);
    try {
      await api.quickLog({ id, from, multiplier, meal_type: meal });
      setNote(`Logged ${label}${multiplier === 1 ? "" : ` x${multiplier}`}`);
      loadRecent();
      loadQuick();
    } catch (e) {
      setNote(e instanceof ApiError ? `Couldn't log it (API ${e.status})` : (e as Error).message);
    }
  }

  async function runSearch(q: string) {
    if (q.trim().length < 2) return;
    setResults("searching");
    try {
      const r = await api.searchFoods(q.trim());
      setResults(r.items);
    } catch {
      setResults([]);
    }
  }

  const onBarcode = useCallback(async (code: string) => {
    setScanning(false);
    setSearch(code);
    setResults("searching");
    try {
      const r = await api.foodByBarcode(code);
      setResults([r.item]);
    } catch (e) {
      setResults([]);
      setNote(e instanceof ApiError && e.status === 404 ? `Barcode ${code} isn't in Open Food Facts` : "Lookup failed");
    }
  }, []);

  /** Logs an Open Food Facts product at a chosen weight, from its per-100 g numbers. */
  async function logProduct(item: OffItem, grams: number, keep: boolean) {
    const per100 = item.per_100g;
    const factor = grams / 100;
    const scaled = (v: number | null) => (v == null ? undefined : Math.round(v * factor * 10) / 10);
    const label = [item.brand, item.name].filter(Boolean).join(" ") + ` (${grams} g)`;
    const body = {
      description: label,
      meal_type: meal,
      calories_kcal: Math.round((per100.calories_kcal ?? 0) * factor),
      protein_g: scaled(per100.protein_g),
      carbs_g: scaled(per100.carbs_g),
      fat_g: scaled(per100.fat_g),
      fiber_g: scaled(per100.fiber_g),
      sugar_g: scaled(per100.sugar_g),
      sodium_mg: scaled(per100.sodium_mg),
    };
    setNote(null);
    try {
      await api.postFood({ ...body, idempotency_key: crypto.randomUUID() });
      if (keep) {
        await api.addFavourite({ ...body, barcode: item.barcode, serving_note: item.serving_note });
      }
      setNote(`Logged ${label}`);
      loadRecent();
      loadQuick();
    } catch (e) {
      setNote(e instanceof ApiError ? `Couldn't log it (API ${e.status})` : (e as Error).message);
    }
  }

  /** Camera straight to the queue: no key, parsed on the PC like typed text. */
  async function onQueuePhoto(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setNote("Uploading photo…");
    try {
      const blob = await downsize(file);
      await api.queueFoodPhoto(blob, meal, text.trim() || undefined);
      setText("");
      setNote("Photo queued. It will be read once the parser on the PC picks it up.");
      loadQuick();
    } catch (e) {
      setNote(e instanceof ApiError ? `Upload failed (API ${e.status})` : (e as Error).message);
    }
  }

  async function sendToParser() {
    if (text.trim().length < 2) return;
    setNote(null);
    try {
      await api.queueFood(text.trim(), meal);
      setText("");
      setNote("Queued. It will appear once the parser on the PC picks it up.");
      loadQuick();
    } catch (e) {
      setNote(e instanceof ApiError ? `Couldn't queue it (API ${e.status})` : (e as Error).message);
    }
  }

  function aiErrorMessage(e: unknown): string {
    if (e instanceof ApiError) {
      const code = (e.body as { error?: string } | null)?.error;
      if (e.status === 503 || code === "ai_not_configured") {
        return "AI logging isn't set up yet (no Anthropic key on the server).";
      }
      if (code === "could_not_parse_meal") return "Couldn't read that — try a clearer photo or describe it.";
      return `API ${e.status}`;
    }
    return (e as Error).message;
  }

  async function onPhoto(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!file) return;
    setAiBusy("photo");
    setAiErr(null);
    try {
      const blob = await downsize(file);
      const res = await api.foodPhoto(blob, meal);
      setPending(res);
    } catch (err) {
      setAiErr(aiErrorMessage(err));
    } finally {
      setAiBusy(null);
    }
  }

  async function onVoice() {
    if (!voiceText.trim()) return;
    setAiBusy("voice");
    setAiErr(null);
    try {
      const res = await api.foodVoice(voiceText.trim(), meal);
      setPending(res);
    } catch (err) {
      setAiErr(aiErrorMessage(err));
    } finally {
      setAiBusy(null);
    }
  }

  async function submitManual(e: React.FormEvent) {
    e.preventDefault();
    const calNum = Number(cal);
    if (!desc.trim() || !Number.isFinite(calNum) || calNum < 0) {
      setErr("Description and calories required");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await api.postFood({
        description: desc.trim(),
        meal_type: meal,
        calories_kcal: calNum,
        protein_g: protein ? Number(protein) : undefined,
        carbs_g: carbs ? Number(carbs) : undefined,
        fat_g: fat ? Number(fat) : undefined,
        idempotency_key: idemKey,
      });
      nav("/");
    } catch (e) {
      setErr(e instanceof ApiError ? `API ${e.status}` : (e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-2xl animate-fade-in">
      <PageHeader title="Food" eyebrow="Log a meal" />

      {/* Meal selector shared by all three logging methods */}
      <div className="card-pad mb-4">
        <span className="field-label">Meal</span>
        <div className="mt-1.5 flex gap-2">
          {MEALS.map((m) => (
            <button
              type="button"
              key={m}
              onClick={() => setMeal(m)}
              className={`flex-1 rounded-md border px-2 py-2 text-sm capitalize transition duration-fast ${
                meal === m
                  ? "border-accent bg-accent text-white"
                  : "border-ink-line bg-surface text-ink-muted hover:border-ink-subtle/60"
              }`}
            >
              {m}
            </button>
          ))}
        </div>
      </div>

      {note && <div className="card-pad mb-4 text-sm text-ink">{note}</div>}

      {/* One tap for anything eaten before. Most days are repeats. */}
      {quick && (quick.favourites.length > 0 || quick.recents.length > 0) && (
        <div className="card-pad mb-4">
          <span className="field-label">Quick add</span>
          <div className="mt-2 space-y-1.5">
            {quick.favourites.map((f) => (
              <QuickRow
                key={`fav-${f.id}`}
                title={f.description}
                sub={`${fmtKcal(f.calories_kcal)}${f.serving_note ? ` · ${f.serving_note}` : ""}`}
                starred
                onLog={(m) => logAgain(f.id, "favourite", m, f.description)}
                onStar={() => api.removeFavourite(f.id).then(loadQuick)}
              />
            ))}
            {quick.recents.map((r) => (
              <QuickRow
                key={`log-${r.id}`}
                title={r.description}
                sub={`${r.times}x · last ${fmtTimeShort(r.last_logged)}`}
                onLog={(m) => logAgain(r.id, "log", m, r.description)}
                onStar={() => api.addFavourite({ from_log_id: r.id }).then(loadQuick)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Open Food Facts: real label numbers, no key needed. */}
      <div className="card-pad mb-4">
        <span className="field-label">Search foods or scan a barcode</span>
        <div className="mt-2 flex gap-2">
          <input
            className="input flex-1"
            value={search}
            placeholder="weet-bix, greek yoghurt, mi goreng"
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && runSearch(search)}
          />
          <button type="button" className="btn-secondary shrink-0 px-3" onClick={() => runSearch(search)}>
            Search
          </button>
          <button type="button" className="btn-secondary shrink-0 px-3" onClick={() => setScanning(true)}>
            Scan
          </button>
        </div>

        {results === "searching" && <div className="mt-3 text-sm text-ink-subtle">Searching…</div>}
        {Array.isArray(results) && results.length === 0 && (
          <div className="mt-3 text-sm text-ink-subtle">Nothing found. Try a brand name, or describe it below.</div>
        )}
        {Array.isArray(results) && results.length > 0 && (
          <ul className="mt-3 divide-y divide-ink-line-soft">
            {results.map((item, i) => (
              <ProductRow key={`${item.barcode ?? item.name}-${i}`} item={item} onLog={logProduct} />
            ))}
          </ul>
        )}
      </div>

      {/* Free text or a photo, parsed on the workstation under the Claude subscription. */}
      <div className="card-pad mb-4">
        <span className="field-label">Photograph it, or describe it</span>
        <input ref={queuePhotoRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onQueuePhoto} />
        <p className="mt-2 text-xs text-ink-subtle">
          Say what it is first — a photo alone can read yoghurt as cream. Then take the picture, and
          it works out how much.
        </p>
        <input
          className="input mt-2 w-full"
          value={text}
          placeholder="greek yoghurt, large bowl"
          onChange={(e) => setText(e.target.value)}
        />
        <button type="button" onClick={() => queuePhotoRef.current?.click()} className="btn-accent mt-2 w-full py-3 text-base">
          📷 Photograph the meal
        </button>
        <textarea
          className="input mt-3 h-20 w-full"
          value={text}
          placeholder="or type the whole meal: 2 boiled eggs, 3/4 cup oats with milk, an apple"
          onChange={(e) => setText(e.target.value)}
        />
        <div className="mt-2 flex items-center justify-between gap-3">
          <p className="text-xs text-ink-subtle">
            Both are read by Claude Code on the PC, so they appear once that machine is running.
            Nothing is lost while it is off.
          </p>
          <button type="button" className="btn-accent shrink-0 px-4" disabled={text.trim().length < 2} onClick={sendToParser}>
            Send
          </button>
        </div>

        {queue.length > 0 && (
          <ul className="mt-3 space-y-1.5">
            {queue.map((q) => (
              <li key={q.id} className="flex items-baseline justify-between gap-2 text-xs">
                {q.status === "pending" ? (
                  // Still unparsed, so a note can still change what it becomes.
                  <input
                    className="input min-w-0 flex-1 py-1 text-xs"
                    defaultValue={q.text === "photo of a meal" ? "" : q.text}
                    placeholder={q.kind === "photo" ? "add a note: what is it?" : q.text}
                    onBlur={async (e) => {
                      const v = e.target.value.trim();
                      if (v && v !== q.text) {
                        await api.noteOnQueued(q.id, v).catch(() => undefined);
                        loadQuick();
                      }
                    }}
                  />
                ) : (
                  <span className="min-w-0 flex-1 truncate text-ink">
                    {q.kind === "photo" ? "📷 " : ""}
                    {q.text}
                  </span>
                )}
                <span className={q.status === "failed" ? "pill-warn" : "pill-neutral"}>
                  {q.status === "pending" ? "waiting for the PC" : q.status === "failed" ? q.error ?? "failed" : "logged"}
                </span>
                <button type="button" className="text-ink-subtle hover:text-loss" onClick={() => api.unqueueFood(q.id).then(loadQuick)}>
                  ✕
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {scanning && <BarcodeScanner onCode={onBarcode} onClose={() => setScanning(false)} />}

      {/* AI: photo + natural-language */}
      <details className="card-pad mb-4 space-y-3">
        <summary className="field-label cursor-pointer">Quick log with AI (needs an Anthropic key)</summary>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={onPhoto}
        />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={aiBusy !== null}
          className="btn-accent w-full py-3 text-base"
        >
          {aiBusy === "photo" ? "Reading photo…" : "📷 Snap a photo"}
        </button>

        <div className="flex gap-2">
          <input
            value={voiceText}
            onChange={(e) => setVoiceText(e.target.value)}
            className="input flex-1"
            placeholder="“a flat white and avo toast”"
            onKeyDown={(e) => e.key === "Enter" && onVoice()}
          />
          <button
            type="button"
            onClick={onVoice}
            disabled={aiBusy !== null || !voiceText.trim()}
            className="btn-secondary shrink-0 px-4"
          >
            {aiBusy === "voice" ? "…" : "Log"}
          </button>
        </div>
        {aiErr && <div className="text-sm text-loss">{aiErr}</div>}
        <p className="text-xs text-ink-subtle">
          AI estimates are always shown for you to confirm or correct before saving.
        </p>
      </details>

      {/* Manual entry */}
      <details className="card-pad mb-4">
        <summary className="field-label cursor-pointer">Enter manually</summary>
        <form onSubmit={submitManual} className="mt-3 space-y-4">
          <Field label="Description">
            <input value={desc} onChange={(e) => setDesc(e.target.value)} className="input" placeholder="Chicken sandwich" />
          </Field>
          <Field label="Calories (kcal)">
            <input inputMode="numeric" value={cal} onChange={(e) => setCal(e.target.value)} className="input" placeholder="420" />
          </Field>
          <div className="grid grid-cols-3 gap-2">
            <Field label="Protein g">
              <input inputMode="decimal" value={protein} onChange={(e) => setProtein(e.target.value)} className="input" />
            </Field>
            <Field label="Carbs g">
              <input inputMode="decimal" value={carbs} onChange={(e) => setCarbs(e.target.value)} className="input" />
            </Field>
            <Field label="Fat g">
              <input inputMode="decimal" value={fat} onChange={(e) => setFat(e.target.value)} className="input" />
            </Field>
          </div>
          {err && <div className="text-sm text-loss">{err}</div>}
          <button disabled={busy} className="btn-secondary w-full py-3">
            {busy ? "Saving…" : "Save manual entry"}
          </button>
        </form>
      </details>

      <h2 className="section-title mt-8 mb-3">Recent</h2>
      {recent && recent.length === 0 ? (
        <EmptyState title="Nothing logged yet">
          Snap a photo, describe a meal, or enter one manually above.
        </EmptyState>
      ) : (
        <ul className="card divide-y divide-ink-line-soft px-4">
          {(recent ?? []).map((f) => (
            <li key={f.id} className="flex items-baseline justify-between gap-3 py-2.5">
              {f.photo_r2_key && (
                <img
                  src={api.foodPhotoUrl(f.id)}
                  alt=""
                  loading="lazy"
                  className="h-10 w-10 shrink-0 self-center rounded-md border border-ink-line object-cover"
                />
              )}
              <div className="min-w-0">
                <div className="truncate text-sm text-ink">{f.description}</div>
                <div className="mt-0.5 text-xs text-ink-subtle">
                  <span className="capitalize">{f.meal_type ?? "—"}</span> · {fmtTimeShort(f.logged_at)}
                  {f.confirmed === 0 ? <span className="pill-warn ml-1.5">pending</span> : null}
                </div>
              </div>
              <div className="amount-spend ml-3 shrink-0 text-sm">{fmtKcal(f.calories_kcal)}</div>
            </li>
          ))}
        </ul>
      )}

      {pending && (
        <ConfirmFoodModal
          pendingId={pending.id}
          parse={pending.parse}
          defaultMeal={meal}
          onDone={() => {
            setPending(null);
            setVoiceText("");
            loadRecent();
          }}
          onCancel={() => {
            setPending(null);
            loadRecent();
          }}
        />
      )}
    </div>
  );
}

/** One saved or recent meal: tap to log it again, at half, once or double. */
function QuickRow({
  title,
  sub,
  starred,
  onLog,
  onStar,
}: {
  title: string;
  sub: string;
  starred?: boolean;
  onLog: (multiplier: number) => void;
  onStar: () => void;
}) {
  return (
    <div className="flex items-center gap-2 rounded-md border border-ink-line px-3 py-2">
      <button type="button" className="min-w-0 flex-1 text-left" onClick={() => onLog(1)}>
        <div className="truncate text-sm text-ink">{title}</div>
        <div className="text-xs text-ink-subtle">{sub}</div>
      </button>
      {[0.5, 1, 2].map((m) => (
        <button
          key={m}
          type="button"
          onClick={() => onLog(m)}
          className="rounded-md px-2 py-1 text-xs font-medium text-ink-muted hover:bg-accent/10 hover:text-accent"
        >
          {m === 0.5 ? "½" : `x${m}`}
        </button>
      ))}
      <button
        type="button"
        onClick={onStar}
        title={starred ? "Remove from favourites" : "Keep as a favourite"}
        className={`px-1 text-sm ${starred ? "text-accent" : "text-ink-subtle hover:text-accent"}`}
      >
        {starred ? "★" : "☆"}
      </button>
    </div>
  );
}

/** A product from the database, logged at a serving or a weight you choose. */
function ProductRow({
  item,
  onLog,
}: {
  item: OffItem;
  onLog: (item: OffItem, grams: number, keep: boolean) => void;
}) {
  const servingG = item.serving_g ?? null;
  const [grams, setGrams] = useState(String(servingG ?? 100));
  const g = Number(grams) || 0;
  const per = (n: OffNutrients) => n.calories_kcal;
  const kcal = per(item.per_100g) != null ? Math.round((per(item.per_100g) as number) * (g / 100)) : null;

  return (
    <li className="py-2.5">
      <div className="flex items-baseline justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate text-sm text-ink">
            {item.brand ? <span className="text-ink-muted">{item.brand} </span> : null}
            {item.name}
          </div>
          <div className="text-xs text-ink-subtle">
            {per(item.per_100g)} kcal / 100 g
            {item.serving_note ? ` · serving ${item.serving_note}` : ""}
          </div>
        </div>
        <div className="shrink-0 font-display tabular-nums text-sm text-ink">{kcal != null ? `${kcal} kcal` : "—"}</div>
      </div>
      <div className="mt-1.5 flex items-center gap-2">
        <input
          className="input w-20 py-1 text-sm"
          inputMode="numeric"
          value={grams}
          onChange={(e) => setGrams(e.target.value.replace(/[^\d.]/g, ""))}
        />
        <span className="text-xs text-ink-subtle">grams</span>
        {servingG && (
          <button type="button" className="text-xs text-ink-muted hover:text-accent" onClick={() => setGrams(String(servingG))}>
            1 serving ({servingG} g)
          </button>
        )}
        <div className="flex-1" />
        <button type="button" className="btn-secondary px-3 py-1 text-xs" disabled={g <= 0} onClick={() => onLog(item, g, true)}>
          Log &amp; keep
        </button>
        <button type="button" className="btn-accent px-3 py-1 text-xs" disabled={g <= 0} onClick={() => onLog(item, g, false)}>
          Log
        </button>
      </div>
    </li>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="field-label">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
